/**
 * Session management utilities for chat interface
 */

// Cookie Domain attribute must include a leading dot so the cookie is shared
// across app.{property}.com (this SPA) and {property}.com (the marketing site
// that hosts the Cordiant website widget). Without this, document.cookie reads
// on the marketing origin return nothing and the widget cannot detect auth.
const SESSION_COOKIE_DOMAIN = import.meta.env.VITE_SESSION_COOKIE_DOMAIN as string | undefined;
const SESSION_COOKIE_MAX_AGE = 60 * 60 * 24;

const buildCookieAttributes = (maxAge: number): string => {
  const parts = [`Path=/`, `Max-Age=${maxAge}`, `SameSite=Lax`];
  if (window.location.protocol === 'https:') parts.push('Secure');
  if (SESSION_COOKIE_DOMAIN) parts.push(`Domain=${SESSION_COOKIE_DOMAIN}`);
  return parts.join('; ');
};

export const setSessionCookie = (token: string): void => {
  document.cookie = `session_token=${token}; ${buildCookieAttributes(SESSION_COOKIE_MAX_AGE)}`;
};

export const clearSessionCookie = (): void => {
  const base = `session_token=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT`;
  // Host-only delete — covers a cookie set with no Domain attribute.
  document.cookie = base;
  // The backend sets session_token domain-scoped (Domain=.{registrable-domain})
  // so the marketing widget on {property}.com and this SPA on app.{property}.com
  // share it. A delete only removes a cookie whose Domain matches — and this is a
  // shared, multi-tenant build with no single hardcoded domain (different hotels
  // run on different domains), so we can't bake one in. Instead, issue the delete
  // at every parent scope of the current host; exactly one matches whatever the
  // backend set. The public-suffix scope (e.g. ".com") is rejected by the browser
  // and is harmless. Without this, the domain-scoped cookie survives the clear,
  // getSessionToken() keeps returning the stale token on the next load, and the
  // app re-enters the session-expired → reload cycle forever.
  const parts = window.location.hostname.split('.');
  for (let i = 0; i < parts.length - 1; i++) {
    document.cookie = `${base}; Domain=.${parts.slice(i).join('.')}`;
  }
  // Honor an explicit build-time override if one is configured.
  if (SESSION_COOKIE_DOMAIN) document.cookie = `${base}; Domain=${SESSION_COOKIE_DOMAIN}`;
};

/**
 * Extract session token from cookies
 */
export const getSessionToken = (): string | null => {
  const sessionCookie = document.cookie
    .split(';')
    .find(cookie => cookie.trim().startsWith('session_token='));
  return sessionCookie ? sessionCookie.split('=')[1].trim() : null;
};

/**
 * Clear all session data and redirect to home
 * Uses full page reload to ensure App.tsx re-initializes with fresh session state
 */
export const clearSessionAndRedirect = (navigate?: (path: string) => void): void => {
  console.log('🧹 Clearing session and redirecting to home...');
  localStorage.clear();
  // sessionStorage holds the trust session (`cordiantone_session`) which
  // session.service.loadPersistedSession() re-hydrates on next page load.
  // If we skip this, the stale session comes back with old inactivity
  // timestamps and instantly fires session-expired again — visible in
  // iframe embeds as a Session Expired dialog loop after the user clicks
  // Sign In. localStorage.clear() alone is not enough; sessionStorage
  // needs to go too.
  try { sessionStorage.clear(); } catch { /* storage locked — best effort */ }
  clearSessionCookie();

  // Use full page reload to ensure App.tsx session state is reset
  // navigate('/') doesn't work because it doesn't update App.tsx isSessionValid state
  window.location.href = '/';
};

/**
 * Validate if user action should be allowed based on session and connection state
 */
export const validateUserAction = (
  isSessionInvalid: boolean,
  socketDisconnected: boolean,
  isSocketConnected: boolean
): boolean => {
  if (isSessionInvalid) {
    console.warn('🚫 Blocking action - session is invalid');
    return false;
  }
  
  if (socketDisconnected && !isSocketConnected) {
    console.warn('🚫 Blocking action - socket is disconnected');
    return false;
  }
  
  return true;
};